import { Color } from "@const/color.constants";
import type { ColorScale } from "plotly.js";
import { formatRgb, parse, rgb, wcagLuminance, type Rgb } from "culori";
import { ThemeUtil } from "./ThemeUtil";

export class ColorUtil {

  /**
   * Graph traces colors
   */
  static readonly colorPalette: string[] = Color.colors;

  /**
   * Color CSS value for no data (heatmap, deleted items)
   */
  static readonly NO_DATA_COLOR = 'lightgray';

  /**
   * Color CSS value for total sum of area plots
   */
  static readonly TOTAL_COLOR = 'black';

  /** Status icon/text colors as theme tokens, so they follow light and dark mode */
  static readonly STATUS_COLOR = {
    pass: 'var(--ant-color-success)',
    fail: 'var(--ant-color-error)',
    warn: 'var(--xm-color-warning)',
    canceled: 'var(--ant-color-text-tertiary)',
  };

  private static readonly COLOR_SCALE: ColorScale = [[0, 'green'], [0.25, 'lightgreen'], [0.5, 'yellow'], [0.75, 'orange'], [1, 'red']];

  private static readonly COLOR_SCALE_DIM: ColorScale = [[0, 'darkgreen'], [0.25, 'green'], [0.5, 'gold'], [0.75, 'darkorange'], [1, 'darkred']];

  /** W3C perceived-brightness threshold (0-255) above which black text reads better */
  private static readonly BRIGHTNESS_CROSSOVER = 125;

  /** WCAG relative luminance where contrast against black equals contrast against white */
  private static readonly LUMINANCE_CROSSOVER = 0.179;

  /**
   * Get scaled color array from the color scale of the theme active when the page loaded
   */
  static readonly scaleColor: (value: number) => number[] = ColorUtil.scaleOf(ColorUtil.getColorScale());

  private static readonly scaleColorLight: (value: number) => number[] = ColorUtil.scaleOf(ColorUtil.COLOR_SCALE);

  private static readonly scaleColorDim: (value: number) => number[] = ColorUtil.scaleOf(ColorUtil.COLOR_SCALE_DIM);

  /**
   * The heatmap colour of a 0-100 value on the scale of the theme given, not the one that was on
   * when the page loaded (scaleColor): a caller that passes the theme redraws on a light/dark switch.
   */
  static scaleColorFor(value: number, dark: boolean = ThemeUtil.isDarkMode()): number[] {
    return dark ? ColorUtil.scaleColorDim(value) : ColorUtil.scaleColorLight(value);
  }

  private static scaleOf(scale: ColorScale): (value: number) => number[] {
    const stops = scale as [number, string][];
    return ColorUtil.makeScaleColor(stops.map(([stop]) => stop * 100), stops.map(([, color]) => color));
  }

  /**
   * plotly.js/src/components/colorscale/helpers
   */
  private static makeScaleColor(domain: number[], colors: string[]) {
    const range = colors.map(ColorUtil.toRgbaArray);
    return (value: number): number[] => {
      if (value <= domain[0]) return [...range[0]];
      const last = domain.length - 1;
      if (value >= domain[last]) return [...range[last]];
      let i = 1;
      while (value > domain[i]) i++;
      const t = (value - domain[i - 1]) / (domain[i] - domain[i - 1]);
      return range[i - 1].map((channel, c) => channel + t * (range[i][c] - channel));
    };
  }

  /**
   * [r, g, b] in 0-255 plus alpha in 0-1; black for an unparsable color
   */
  private static toRgbaArray(color: string): number[] {
    const c: Rgb = rgb(color) ?? { mode: 'rgb', r: 0, g: 0, b: 0 };
    return [Math.round(c.r * 255), Math.round(c.g * 255), Math.round(c.b * 255), c.alpha ?? 1];
  }

  /**
   * Array containing arrays mapping a normalized value to an rgb, rgba, hex, hsl, hsv, or named color string.
   * At minimum, a mapping for the lowest (0) and highest (1) values are required.
   */
  static getColorScale(dark: boolean = ThemeUtil.isDarkMode()) {
    return dark ? ColorUtil.COLOR_SCALE_DIM : ColorUtil.COLOR_SCALE;
  }

  /**
   * Convert color array to rgba string
   * @param colorArray [r, g, b] in 0-255 plus optional alpha in 0-1
   */
  static colorArray2rgba(colorArray: number[]) {
    const [r, g, b, alpha] = colorArray;
    return formatRgb({ mode: 'rgb', r: r / 255, g: g / 255, b: b / 255, alpha });
  }

  /**
   * Get contrast color (black or white) for provided color array
   * @param color [r, g, b] in 0-255
   */
  static contrastColor(color: number[]) {
    const [r, g, b] = color;
    const brightness = Math.round((r * 299 + g * 587 + b * 114) / 1000);
    return brightness > ColorUtil.BRIGHTNESS_CROSSOVER ? 'black' : 'white';
  }

  /**
   * Get readable text color (black or white) for any CSS color string (hex, rgb(a), hsl, named, …)
   * @param color any CSS color string
   * @returns '#000000' or '#ffffff'
   */
  static contrastText(color: string) {
    return ColorUtil.luminance(color) > ColorUtil.LUMINANCE_CROSSOVER ? '#000000' : '#ffffff';
  }

  /**
   * WCAG relative luminance; 0 (black) for an unparsable color
   */
  static luminance(color: string) {
    const parsed = parse(color);
    return parsed ? wcagLuminance(parsed) : 0;
  }
}
